import { useContext, useCallback, useState, useEffect } from 'react';
import { AuthContext } from './AuthProvider';
import { setToken as saveTokenToStorage } from '../services/auth';
import {
  AuthenticationResult,
  PopupRequest,
  SilentRequest,
} from '@azure/msal-browser';

interface User {
  displayName: string | undefined;
  mail: string | undefined;
  oid: string | undefined;
}

interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

/**
 * Hook to use MSAL authentication in components
 * 
 * Usage:
 * const { user, isAuthenticated, login, logout, getAccessToken } = useMsal();
 */
export function useMsal(): UseAuthReturn {
  const context = useContext(AuthContext);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (!context) {
    throw new Error('useMsal must be used within AuthProvider');
  }

  const { msalInstance, isInitialized, isDevMode } = context;
  const apiScope = import.meta.env.VITE_API_SCOPE;

  // Check authentication status and get user info
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    const checkAuth = async () => {
      try {
        // DEV MODE: Auto-authenticate as LocalDeveloper
        if (isDevMode) {
          setUser({
            displayName: 'LocalDeveloper',
            mail: 'dev@local.test',
            oid: '00000000-0000-0000-0000-000000000001',
          });
          // Store a dummy token for dev mode
          saveTokenToStorage('dev-token-ignored-by-backend');
          setIsLoading(false);
          return;
        }

        // ENTRA MODE: Handle redirect, then check accounts
        if (!msalInstance) {
          setIsLoading(false);
          return;
        }

        const redirectResponse = await msalInstance.handleRedirectPromise();

        if (redirectResponse) {
          setUser({
            displayName: redirectResponse.account?.name,
            mail: redirectResponse.account?.username,
            oid: redirectResponse.account?.localAccountId,
          });
          if (redirectResponse.idToken) {
            saveTokenToStorage(redirectResponse.idToken);
          }
          setIsLoading(false);
          return;
        }

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          setUser({
            displayName: account.name,
            mail: account.username, // Usually email
            oid: account.localAccountId,
          });
          
          // Try to get and cache a token silently
          try {
            const silentRequest: SilentRequest = {
              scopes: [apiScope],
              account: account,
            };
            const response = await msalInstance.acquireTokenSilent(silentRequest);
            if (response.idToken) {
              saveTokenToStorage(response.idToken);
            }
          } catch (err) {
            // Intentionally silent - token refresh failures are expected and will retry on next API call
            console.log('[useMsal] Silent token acquisition failed, will retry on next API call', err);
          }
        } else {
          setUser(null);
        }
      } catch (err: any) {
        console.error('[useMsal] Error checking auth:', err);
        setError(err?.message || 'Failed to check authentication');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
  }, [isInitialized, msalInstance, isDevMode]);

  const login = useCallback(async () => {
    // DEV MODE: No-op
    if (isDevMode) {
      setUser({
        displayName: 'LocalDeveloper',
        mail: 'dev@local.test',
        oid: '00000000-0000-0000-0000-000000000001',
      });
      // Store a dummy token for dev mode
      saveTokenToStorage('dev-token-ignored-by-backend');
      return;
    }

    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    try {
      setError(null);
      setIsLoading(true);

      await msalInstance.loginRedirect({
        scopes: ['openid', 'profile', 'email', 'User.Read', apiScope],
        prompt: 'select_account',
      });
      // Page navigates away — nothing below runs
    } catch (err: any) {
      if (err?.errorCode === 'interaction_in_progress' || err?.message?.includes('interaction_in_progress')) {
        console.warn('[useMsal] Interaction already in progress');
        setError('A login is already in progress. Please wait.');
        setIsLoading(false);
        return;
      }
      const errorMessage = err?.message || 'Login failed';
      console.error('[useMsal] Login error:', err);
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [msalInstance, isDevMode]);

  const logout = useCallback(async () => {
    // DEV MODE: No-op
    if (isDevMode) {
      setUser(null);
      return;
    }

    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    try {
      setError(null);
      setIsLoading(true);

      const logoutRequest = {
        postLogoutRedirectUri: globalThis.location.origin,
      };

      await msalInstance.logout(logoutRequest);
      setUser(null);
    } catch (err: any) {
      const errorMessage = err?.message || 'Logout failed';
      console.error('[useMsal] Logout error:', err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [msalInstance, isDevMode]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    // DEV MODE: Return a dummy token (backend ignores it in dev mode)
    if (isDevMode) {
      return 'dev-token-ignored-by-backend';
    }

    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No user account found');
      }

      const account = accounts[0];
      // ALWAYS force refresh due to system date issues
      const silentRequest: SilentRequest = {
        scopes: [apiScope],
        account: account,
        forceRefresh: true, // Always get fresh token to avoid expired token issues
      };

      const response: AuthenticationResult = await msalInstance.acquireTokenSilent(silentRequest);
      console.log('[useMsal] Token acquired, expires:', new Date(response.expiresOn || 0).toISOString());
      
      // Return access token for API authentication
      return response.accessToken;
    } catch (err: any) {
      console.error('[useMsal] Error acquiring token:', err);
      
      // If silent request fails, try popup
      try {
        const popupRequest: PopupRequest = {
          scopes: [apiScope],
        };
        const response: AuthenticationResult = await msalInstance.acquireTokenPopup(popupRequest);
        return response.accessToken;
      } catch (popupErr: any) {
        const errorMessage = popupErr?.message || 'Failed to acquire access token';
        console.error('[useMsal] Token acquisition failed:', popupErr);
        throw new Error(errorMessage);
      }
    }
  }, [msalInstance, isDevMode]);

  const isAuthenticated = !!user;

  return {
    user,
    isLoading,
    isAuthenticated,
    error,
    login,
    logout,
    getAccessToken,
  };
}
